import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { RouterModule } from '@angular/router';

/** The seven role marks the badge on a team portrait can show. */
export type MtIcon = 'crown' | 'brief' | 'code' | 'php' | 'ng' | 'react' | 'user';

/** One roster record as authored below. */
interface MtRecord {
  img: string;
  name: string;
  designation: string;
  /** Two to three lines on what the person does. */
  blurb: string;
}

/** A roster record prepared for the template. */
interface MtPerson extends MtRecord {
  /** Monogram shown behind the portrait while (or if) the photo never lands. */
  initials: string;
  /** Badge mark, derived from the designation. */
  icon: MtIcon;
  /** Accessible name for the badge. */
  iconLabel: string;
  /** 1 – 3, the brand colour this card reads from (pink / purple / indigo). */
  accent: number;
  /** Cascade offset in ms so a row of cards lands one after the other. */
  delay: number;
  /** Reveal key this card's animation state is looked up by. */
  key: string;
}

/** Designations that belong in the Leadership section, in display order. */
const LEADERSHIP_ROLES = ['director', 'ceo', 'manager'];

/**
 * MEET THE TEAM
 * ----------------------------------------------------------------------------
 * Premium SaaS-style team page on the ResilienceSoft palette: a soft-gradient
 * hero, a featured Leadership block, then the rest of the roster in a four-up
 * grid. Photos, names and designations are the existing roster; the one-line
 * role summaries are new, since the layout calls for them.
 *
 * Motion is Angular animations only (no GSAP here): every reveal is a state
 * change driven by an IntersectionObserver, with a per-card delay param
 * standing in for a stagger. On the server and under `prefers-reduced-motion`
 * every element starts in its `shown` state, so the content is always there.
 */
@Component({
  selector: 'app-meettheteam',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './meettheteam.component.html',
  styleUrl: './meettheteam.component.scss',
  animations: [
    // Cards: fade up and settle. `delay` gives the row-by-row cascade.
    trigger('mtCard', [
      state('hidden', style({ opacity: 0, transform: 'translateY(26px) scale(0.97)' })),
      state('shown', style({ opacity: 1, transform: 'none' })),
      transition(
        'hidden => shown',
        [animate('460ms {{ delay }}ms cubic-bezier(0.22, 1, 0.36, 1)')],
        { params: { delay: 0 } }
      ),
    ]),
    // Hero copy, section headings and the closing CTA: fade + slide.
    trigger('mtReveal', [
      state('hidden', style({ opacity: 0, transform: 'translateY(22px)' })),
      state('shown', style({ opacity: 1, transform: 'none' })),
      transition(
        'hidden => shown',
        [animate('500ms {{ delay }}ms cubic-bezier(0.22, 1, 0.36, 1)')],
        { params: { delay: 0 } }
      ),
    ]),
  ],
})
export class MeettheteamComponent implements AfterViewInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Adds the hover / spotlight affordances only where they make sense. */
  @HostBinding('class.mt-anim') animated = false;

  /**
   * Keys already scrolled into view. A signal, not a plain Set: the observer
   * callback can land outside a change-detection tick, and on an OnPush
   * component only a signal write reliably schedules the re-render.
   */
  private readonly revealed = signal<ReadonlySet<string>>(new Set());
  private observer?: IntersectionObserver;
  private pointerCleanup: Array<() => void> = [];

  meetTheTeam: MtRecord[] = [
    {
      img: 'https://images.prismic.io/kurmishadi/530015ce-677b-4bbb-84d4-d0dc7605bce9_pankaj.jpg?auto=format,compress?auto=compress,format',
      name: 'Pankaj Mishra',
      designation: 'Director  CEO',
      blurb: 'Visionary leader with a passion for technology and product innovation.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/d81c324c-501f-4d0d-8d41-40ec4e795b5f_prateek.jpg?auto=format,compress?auto=compress,format',
      name: 'Prateek Saluja',
      designation: 'Director  CEO',
      blurb: 'Driving strategy, growth and operational excellence across the company.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlWn3aWtHYXtT14Q_sushant-saluja.webp?auto=format,compress?auto=compress,format',
      name: 'Sushant Saluja',
      designation: 'Functional Manager',
      blurb: 'Ensuring seamless delivery and outstanding client experiences on every project.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/65de2683-a34a-4e43-8804-a5fef77bdd56_deepak.jpg?auto=format,compress?auto=compress,format',
      name: 'Deepak Verma',
      designation: 'Functional Manager',
      blurb: 'Keeping teams aligned and turning requirements into predictable outcomes.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/0fc62679-7b52-41c8-ae81-51d7ec8a01e7_irshad.jpg?auto=format,compress?auto=compress,format',
      name: 'Irshad Alam',
      designation: 'Senior Web Designer',
      blurb: 'Crafting engaging, user-friendly websites that deliver exceptional digital experiences.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/63828c9d-5c63-4022-a098-2ab1bc80e410_shreya.jpg?auto=format,compress?auto=compress,format',
      name: 'Shreya Gupta',
      designation: 'Senior PHP Developer',
      blurb: 'Building robust, secure and scalable web applications with clean code.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlWoJ6WtHYXtT14d_arzoo.jpeg?auto=format,compress?auto=compress,format',
      name: 'Arzoo Khan',
      designation: 'Senior PHP Developer ',
      blurb: 'Developing dynamic solutions and features that drive business growth.',
    },

    {
      img: 'https://images.prismic.io/kurmishadi/45e2f9ca-80fb-4985-bc7b-86ab530e9db5_ankita.jpeg?auto=format,compress?auto=compress,format',
      name: 'Ankita Choudhary',
      designation: 'Senior Angular Developer',
      blurb: 'Building scalable Angular applications with performance and usability in mind.',
    },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/ZlWp9aWtHYXtT158_devender.webp?auto=format,compress?auto=compress,format',
    //   name: 'Devender Sharma',
    //   designation: 'Senior .Net Developer',
    // },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/ZlWq5qWtHYXtT16k_vaibhav.webp?auto=format,compress?auto=compress,format',
    //   name: 'Vaibhav Dubay',
    //   designation: 'Senior Angular Developer',
    // },
    {
      img: 'https://images.prismic.io/kurmishadi/37e6f9b7-3dac-459e-ae3a-019d264cc6a9_sanya.webp?auto=compress,format=webp,height=200,width=200',
      name: 'Sanya Mishra',
      designation: 'React Native Developer',
      blurb: 'Creating smooth, fast and responsive mobile applications for all platforms.',
    },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/ZlWrQKWtHYXtT16y_hemadri.webp?auto=format,compress?auto=compress,format',
    //   name: 'Hemadri Sahu',
    //   designation: 'Angular Developer',
    // },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlW_oaWtHYXtT2KK_rakhi.jpg?auto=format,compress?auto=compress,format',
      name: 'Rakhi Dutta',
      designation: 'PHP Developer',
      blurb: 'Writing efficient PHP code and delivering high-quality web solutions.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/Zlmkh6WtHYXtT9q6_sandhaya.jpg?auto=format,compress?auto=compress,format',
      name: 'Sandhya Ojha',
      designation: 'Angular Developer',
      blurb: 'Developing dynamic and interactive web applications using Angular.',
    },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/ZlWuTqWtHYXtT19D_rashmi.png?auto=format,compress?auto=compress,format',
    //   name: 'Rashmi Garg',
    //   designation: 'Angular Developer',
    // },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/ZlWpvKWtHYXtT154_niraj.webp?auto=format,compress?auto=compress,format',
    //   name: 'Niraj Shaw',
    //   designation: 'QA',
    // },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/ZlmijKWtHYXtT9po_chiranjeev.jpeg?auto=format,compress?auto=compress,format',
    //   name: 'Chiranjiv Mohanty',
    //   designation: 'React Native Developer',
    // },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/Zl2m5KWtHYXtUBY9_ayush.png?auto=format,compress?auto=compress,format',
    //   name: 'Ayush Ojha',
    //   designation: 'IT Sales',
    // },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlW45KWtHYXtT2GB_roshni.webp?auto=format,compress?auto=compress,format',
      name: 'Roshni Rathore',
      designation: 'PHP Developer',
      blurb: 'Building reliable backends and integrating features that power seamless user experiences.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlXBjaWtHYXtT2MH_praveen.jpg?auto=format,compress?auto=compress,format',
      name: 'Praveen Nirmalkar',
      designation: 'React Native Developer',
      blurb: 'Developing feature-rich mobile apps with clean, maintainable and testable code.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlWrgaWtHYXtT167_twinkle.webp?auto=format,compress?auto=compress,format',
      name: 'Twinkle Dewangan',
      designation: 'Angular Developer',
      blurb: 'Developing modern web solutions and optimizing performance for better outcomes.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/Zl2fPqWtHYXtUBNC_durgesh.JPEG?auto=format,compress?auto=compress,format',
      name: 'Durgesh Gupta',
      designation: 'Web Designer',
      blurb: 'Designing intuitive interfaces and engaging user experiences that convert.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlW3C6WtHYXtT2E7_malti.png?auto=format,compress?auto=compress,format',
      name: 'Malti Kaushik',
      designation: 'Web Designer',
      blurb: 'Creating visually appealing designs that enhance brand and user engagement.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlWr5aWtHYXtT17H_devashish.webp?auto=format,compress?auto=compress,format',
      name: 'Devasheesh Pilai',
      designation: 'Angular Developer',
      blurb: 'Turning complex requirements into clean, component-driven Angular front ends.',
    },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/ZlXAMKWtHYXtT2Kv_yashwant_palke.png?auto=format,compress?auto=compress,format',
    //   name: 'Yashwant Palke',
    //   designation: 'PHP Developer',
    // },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/Zlmj1aWtHYXtT9qr_kalmeshwar.JPEG?auto=format,compress?auto=compress,format',
    //   name: 'Kamleshwar Jangde',
    //   designation: '.Net Developer',
    // },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlXET6WtHYXtT2NZ_Shruti2.png?auto=format,compress?auto=compress,format',
      name: 'Shruti Mourya',
      designation: 'React Native Developer',
      blurb: 'Shipping cross-platform mobile features that feel native on every device.',
    },
    // {
    //   img: 'https://images.prismic.io/kurmishadi/Zlmlx6WtHYXtT9r7_rahul-dewangan.png?auto=format,compress?auto=compress,format',
    //   name: 'Rahul Dewangan',
    //   designation: 'Web Designer',
    // },
    {
      img: 'https://images.prismic.io/kurmishadi/ZlXCJ6WtHYXtT2MY_khushi.jpg?auto=format,compress?auto=compress,format',
      name: 'Khusi Gupta',
      designation: 'React Native Developer',
      blurb: 'Building smooth mobile experiences with a close eye on detail and performance.',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ZuPSBbVsGrYSvUie_IMG-20240905-WA0003.jpg?auto=format,compress',
      name: 'Ranu Yadav',
      designation: 'Office Assistant',
      blurb: 'Keeping the office running smoothly so every team can focus on the work.',
    },
  ];

  /** Directors and managers, shown as large featured cards. */
  readonly leadership: MtPerson[] = this.meetTheTeam
    .filter((m) => MeettheteamComponent.isLeadership(m.designation))
    .map((m, i) => MeettheteamComponent.prepare(m, i, 'lead'));

  /** Everyone else, shown four-up. */
  readonly team: MtPerson[] = this.meetTheTeam
    .filter((m) => !MeettheteamComponent.isLeadership(m.designation))
    .map((m, i) => MeettheteamComponent.prepare(m, i, 'team'));

  constructor() {
    this.animated = this.isBrowser && !this.prefersReducedMotion();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    this.observeReveals();
    this.playHero();
    this.bindSpotlight();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.pointerCleanup.forEach((fn) => fn());
    this.pointerCleanup = [];
  }

  /** Animation state for one reveal target. */
  stateOf(key: string): 'hidden' | 'shown' {
    return !this.animated || this.revealed().has(key) ? 'shown' : 'hidden';
  }

  trackPerson = (_i: number, p: MtPerson): string => p.key;

  /** A portrait the CDN never returns fades out and leaves the monogram. */
  onPortraitError(event: Event): void {
    (event.target as HTMLImageElement).classList.add('is-failed');
  }

  /** Hero copy is above the fold, so it plays on load rather than on scroll. */
  private playHero(): void {
    const keys = ['hero-eyebrow', 'hero-title', 'hero-rule', 'hero-lede'];
    if (!this.animated) {
      this.reveal(keys);
      return;
    }
    // one frame in the `hidden` state first, otherwise there is nothing to
    // animate from
    requestAnimationFrame(() => this.reveal(keys));
  }

  private static isLeadership(designation: string): boolean {
    const role = designation.toLowerCase();
    return LEADERSHIP_ROLES.some((token) => role.includes(token));
  }

  private static prepare(record: MtRecord, index: number, group: string): MtPerson {
    const perRow = group === 'lead' ? 4 : 4;

    return {
      ...record,
      ...MeettheteamComponent.badgeFor(record.designation),
      initials: MeettheteamComponent.initialsFor(record.name),
      accent: (index % 3) + 1,
      delay: (index % perRow) * 80,
      key: `${group}-${index}`,
    };
  }

  /** Designation → badge mark. Keyword order matters: seniority first. */
  private static badgeFor(designation: string): { icon: MtIcon; iconLabel: string } {
    const role = designation.toLowerCase();

    if (role.includes('director') || role.includes('ceo')) {
      return { icon: 'crown', iconLabel: 'Leadership' };
    }
    if (role.includes('manager')) {
      return { icon: 'brief', iconLabel: 'Management' };
    }
    if (role.includes('react')) {
      return { icon: 'react', iconLabel: 'React Native' };
    }
    if (role.includes('angular')) {
      return { icon: 'ng', iconLabel: 'Angular' };
    }
    if (role.includes('php')) {
      return { icon: 'php', iconLabel: 'PHP' };
    }
    if (role.includes('designer') || role.includes('developer')) {
      return { icon: 'code', iconLabel: 'Design and engineering' };
    }
    return { icon: 'user', iconLabel: 'Operations' };
  }

  /** "Sandhya Ojha" → "SO". Stands in until the portrait paints. */
  private static initialsFor(name: string): string {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /** Flips each `[data-mt-reveal]` to its `shown` state as it scrolls in. */
  private observeReveals(): void {
    const targets = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[data-mt-reveal]')
    );
    if (!targets.length) return;

    if (!this.animated || !('IntersectionObserver' in window)) {
      this.reveal(targets.map((el) => el.dataset['mtReveal']!));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const keys: string[] = [];

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const key = (entry.target as HTMLElement).dataset['mtReveal'];
          if (key) keys.push(key);
          this.observer?.unobserve(entry.target);
        });

        this.reveal(keys);
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((el) => this.observer!.observe(el));
  }

  /** Adds keys to the revealed set, replacing it so the signal notifies. */
  private reveal(keys: string[]): void {
    if (!keys.length) return;

    const current = this.revealed();
    const fresh = keys.filter((k) => !current.has(k));
    if (!fresh.length) return;

    this.revealed.set(new Set([...current, ...fresh]));
  }

  /**
   * Pointer spotlight: each card tracks the cursor through two CSS custom
   * properties. Runs on fine pointers only, one rAF per frame, and never
   * touches change detection.
   */
  private bindSpotlight(): void {
    if (!this.animated) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const cards = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('.mt-card')
    );

    cards.forEach((card) => {
      let queued = false;
      let mx = 50;
      let my = 50;

      const apply = () => {
        queued = false;
        card.style.setProperty('--mt-mx', `${mx}%`);
        card.style.setProperty('--mt-my', `${my}%`);
      };

      const onMove = (event: MouseEvent) => {
        const rect = card.getBoundingClientRect();
        mx = ((event.clientX - rect.left) / rect.width) * 100;
        my = ((event.clientY - rect.top) / rect.height) * 100;
        if (queued) return;
        queued = true;
        requestAnimationFrame(apply);
      };

      const onLeave = () => {
        card.style.removeProperty('--mt-mx');
        card.style.removeProperty('--mt-my');
      };

      card.addEventListener('mousemove', onMove, { passive: true });
      card.addEventListener('mouseleave', onLeave, { passive: true });
      this.pointerCleanup.push(() => {
        card.removeEventListener('mousemove', onMove);
        card.removeEventListener('mouseleave', onLeave);
      });
    });
  }
}
